import * as React from 'react'
import { theme } from '../../theme'
import { DimensionsPanel } from '../../image_transform/DimensionsPanel'
import { AiGenerationInputs } from '../../generate_with_ai/AiGenerationInputs'
import { ToolSelector } from '../ToolSelector'
import { SaveAsNewButton } from '../SaveAsNewButton'
import { AspectOption } from '../../dimension_management/DimensionOptions'
import { GenerationType } from '../../generate_with_ai/GenerationTypeSelector'
import { GenerationMode } from '../../generate_with_ai/GenerationModeSelector'
import { EmptySpaceCalculation } from '../../image_transform/types'
import { TDrawingTool } from '../../types'

interface Props {
	selectedAction: string
	controlsWidth: string
	// Adjust tool props
	selectedDimension: AspectOption
	transform: {
		scale: number
		position: { x: number; y: number }
		isDragging: boolean
	}
	emptySpace: EmptySpaceCalculation
	imageWidth: number
	imageHeight: number
	onResetTransform: () => void
	onAcceptTransform: () => void
	getScaledDimensions: (width: number, height: number) => { width: number; height: number }
	setTransform: (transform: { scale: number; position: { x: number; y: number }; isDragging: boolean }) => void
	// Create with AI props
	articleUrls: string
	customPrompt: string
	referenceImage: File | null
	aiError: string | null
	generationType: GenerationType
	aiSelectedDimension: AspectOption
	isGenerating: boolean
	handleArticleUrlsChange: (value: string) => void
	handleCustomPromptChange: (prompt: string) => void
	handleReferenceImageSelect: (file: File) => void
	handleGenerationTypeChange: (type: GenerationType) => void
	handleAiDimensionChange: (dimension: AspectOption) => void
	handleGenerate: () => Promise<void>
	clearError: () => void
	reset: () => void
	generationMode: GenerationMode
	onGenerationModeChange: (mode: GenerationMode) => void
	isImageSuitableForStructure: boolean
	referenceImageDimensions: { width: number; height: number } | null
	registerImageRef: (dimensionId: string, element: HTMLImageElement | null) => void
	registerContainerRef: (dimensionId: string, element: HTMLDivElement | null) => void
	// Tool selector props
	currentTool: TDrawingTool
	isEditing: boolean
	onToolSelect: (tool: TDrawingTool) => void
	hasMask: boolean
	currentPrompt: string
	onUndo: () => void
	onAccept: () => void
	brushSize: number
	onBrushSizeChange: (size: number) => void
	disclaimerText: string
	onDisclaimerTextChange: (text: string) => void
	onDisclaimerAccept: () => void
	isProcessing: boolean
	areAllAspectRatiosValid: boolean
	onSwitchToAdjustTool: () => void
	textAlignment: 'left' | 'center'
	onTextAlignmentChange: (alignment: 'left' | 'center') => void
	onIncreaseFontSize: () => void
	onDecreaseFontSize: () => void
	// Save button props
	onSaveAsNew: () => void
	isLoading: boolean
}

export const RightControlPanel = ({
	selectedAction,
	controlsWidth,
	// Adjust tool props
	selectedDimension,
	transform,
	emptySpace,
	imageWidth,
	imageHeight,
	onResetTransform,
	onAcceptTransform,
	getScaledDimensions,
	setTransform,
	// Create with AI props
	articleUrls,
	customPrompt,
	referenceImage,
	aiError,
	generationType,
	aiSelectedDimension,
	isGenerating,
	handleArticleUrlsChange,
	handleCustomPromptChange,
	handleReferenceImageSelect,
	handleGenerationTypeChange,
	handleAiDimensionChange,
	handleGenerate,
	clearError,
	reset,
	generationMode,
	onGenerationModeChange,
	isImageSuitableForStructure,
	referenceImageDimensions,
	registerImageRef,
	registerContainerRef,
	// Tool selector props
	currentTool,
	isEditing,
	onToolSelect,
	hasMask,
	currentPrompt,
	onUndo,
	onAccept,
	brushSize,
	onBrushSizeChange,
	disclaimerText,
	onDisclaimerTextChange,
	onDisclaimerAccept,
	isProcessing,
	areAllAspectRatiosValid,
	onSwitchToAdjustTool,
	textAlignment,
	onTextAlignmentChange,
	onIncreaseFontSize,
	onDecreaseFontSize,
	// Save button props
	onSaveAsNew,
	isLoading,
}: Props) => {
	return (
		<div
			style={{
				width: controlsWidth,
				borderLeft: `1px solid ${theme.colors.border}`,
				padding: theme.spacing.md,
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.md,
				overflowY: 'auto',
			}}
		>
			{selectedAction === 'create' && (
				<div
					style={{
						backgroundColor: theme.colors.surface,
						borderRadius: theme.borderRadius.sm,
						border: `1px solid ${theme.colors.border}`,
						padding: theme.spacing.md,
					}}
				>
					<h3
						style={{
							margin: 0,
							marginBottom: theme.spacing.md,
							fontSize: theme.typography.sizes.md,
							color: theme.colors.text.primary,
						}}
					>
						Generate Image with AI
					</h3>
					<AiGenerationInputs
						articleUrls={articleUrls}
						customPrompt={customPrompt}
						referenceImage={referenceImage}
						error={aiError}
						generationType={generationType}
						selectedDimension={aiSelectedDimension}
						isGenerating={isGenerating}
						handleArticleUrlsChange={handleArticleUrlsChange}
						handleCustomPromptChange={handleCustomPromptChange}
						handleReferenceImageSelect={handleReferenceImageSelect}
						handleGenerationTypeChange={handleGenerationTypeChange}
						handleDimensionChange={handleAiDimensionChange}
						handleGenerate={handleGenerate}
						clearError={clearError}
						reset={reset}
						generationMode={generationMode}
						onGenerationModeChange={onGenerationModeChange}
						isImageSuitableForStructure={isImageSuitableForStructure}
						referenceImageDimensions={referenceImageDimensions}
						hideDimensionOptions={true}
						registerImageRef={registerImageRef}
						registerContainerRef={registerContainerRef}
					/>
				</div>
			)}

			{/* Calculations Panel - Only show when adjust is selected */}
			{selectedAction === 'adjust' && (
				<DimensionsPanel
					selectedDimension={selectedDimension}
					transform={transform}
					emptySpace={emptySpace}
					imageWidth={imageWidth}
					imageHeight={imageHeight}
					onReturn={onResetTransform}
					onAccept={onAcceptTransform}
					getScaledDimensions={getScaledDimensions}
					setTransform={setTransform}
				/>
			)}

			{/* Tools - Hide in adjust and create modes */}
			{selectedAction !== 'adjust' && selectedAction !== 'create' && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: theme.spacing.md,
						padding: theme.spacing.sm,
						backgroundColor: theme.colors.surface,
						borderRadius: theme.borderRadius.sm,
						border: `1px solid ${theme.colors.border}`,
						flexShrink: 0,
					}}
				>
					<ToolSelector
						currentTool={currentTool}
						isEditing={isEditing}
						onToolSelect={onToolSelect}
						hasMask={hasMask}
						currentPrompt={currentPrompt}
						onUndo={onUndo}
						onAccept={onAccept}
						brushSize={brushSize}
						onBrushSizeChange={onBrushSizeChange}
						disclaimerText={disclaimerText}
						onDisclaimerTextChange={onDisclaimerTextChange}
						onDisclaimerAccept={onDisclaimerAccept}
						isProcessing={isProcessing}
						selectedAction={selectedAction}
						areAllAspectRatiosValid={areAllAspectRatiosValid}
						onSwitchToAdjustTool={onSwitchToAdjustTool}
						textAlignment={textAlignment}
						onTextAlignmentChange={onTextAlignmentChange}
						onIncreaseFontSize={onIncreaseFontSize}
						onDecreaseFontSize={onDecreaseFontSize}
					/>
				</div>
			)}

			{/* Download Button */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					marginTop: 'auto',
					paddingTop: theme.spacing.md,
					borderTop: `1px solid ${theme.colors.border}`,
				}}
			>
				<SaveAsNewButton onClick={onSaveAsNew} disabled={isLoading} />
			</div>
		</div>
	)
}
